/**
 * Management grid - Figma `6377:87997`, and `6373:15870` for the type and the pill.
 *
 * A heading over a five-up row of portraits: photograph, name, role, and a LinkedIn
 * pill under each one.
 *
 * **`shared.person` gained `linkedinUrl`.** The design draws a LinkedIn link under every
 * portrait and the component had nowhere to put the address. The pill only renders when
 * an editor fills it in, so a profile without LinkedIn simply has none. The seed fills in
 * none: they are real people, and an invented address is somebody else's profile.
 *
 * The pills sit at the foot of their cards, level across the row however many lines a
 * role takes - the file gives the text a fixed 147px and pins the pill to its bottom.
 *
 * Five equal columns is what the design shows. It steps down to two and then one rather
 * than squeezing five portraits onto a phone; the Figma file is desktop-only, so the
 * breakpoints are ours.
 */
import { SectionShell } from '../primitives';
import type { ManagementGridSection } from '../../types/sections';
import { Image, SIZES } from '../../components/ui/Image';
import { AppLink } from '../../components/ui/AppLink';
import { offsiteHref } from '../../lib/links';

export default function ManagementGrid({ data }: { data: ManagementGridSection }) {
  const people = data.people ?? [];

  return (
    <SectionShell settings={data.settings} name="management-grid">
      {data.heading ? <h2 className="text-h3 text-ink lg:text-h2">{data.heading}</h2> : null}

      {people.length ? (
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
          {people.map((person, i) => (
            <article key={i} className="flex flex-col gap-4">
              <figure className="m-0 aspect-[3/4] overflow-hidden rounded-s bg-surface-sunken">
                {person.photo ? (
                  <Image
                    media={person.photo}
                    alt={person.photo.alternativeText ?? person.name}
                    sizes={SIZES.third}
                    className="h-full w-full object-cover"
                  />
                ) : null}
              </figure>

              <div className="flex flex-col gap-2">
                <h3 className="text-h4 text-ink-strong">{person.name}</h3>
                {person.role ? <p className="text-body-sm text-ink-muted">{person.role}</p> : null}
                {person.bio ? <p className="text-body-sm text-ink-muted">{person.bio}</p> : null}
              </div>

              {/* 6373:15870: a 1px Grey-900-A outline round a 16px label, 20 by 10 of padding;
                  hovered (Michal Zurek's card in the file) it fills black. The visible word
                  is the file's; the name after it is for a screen reader, which would
                  otherwise hear five links all called "linkedin". */}
              {person.linkedinUrl ? (
                <AppLink
                  to={offsiteHref(person.linkedinUrl)}
                  className="mt-auto inline-flex w-fit items-center rounded-full border border-ink px-5 py-2.5 text-body text-ink transition-colors hover:border-ink-strong hover:bg-ink-strong hover:text-white"
                >
                  linkedin<span className="sr-only">, {person.name}</span>
                </AppLink>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}
    </SectionShell>
  );
}
